import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { aiApi, type AiConversation, type AiModelInfo, type AskUserAnswer, type AttachmentMeta, type PendingApproval, type PendingUserQuestions } from '@/api/ai'
import { ApiError } from '@/api/client'
import { toast } from 'vue-sonner'

export type AiContentBlock =
  | { type: 'thinking'; thinking: string; signature: string }
  | { type: 'text'; text: string }

export type ToolActivity = {
  name: string
  toolUseId?: string
  args?: Record<string, unknown>
  summary?: string
  result?: unknown
  status: 'calling' | 'done'
  startedAt?: number
}

import type { ChatReference, ResourceRef } from '@/lib/resource-links'

export type MessageSegment =
  | { type: 'text'; text: string }
  | { type: 'tool_call'; activity: ToolActivity }
  | { type: 'resource'; refs: ResourceRef[] }
  | { type: 'recovery'; status: { reason: ChatRecoveryReason; continuable: boolean; message?: string } }

export type ChatRecoveryReason = 'max_tokens' | 'iteration_budget' | 'context_full' | 'error' | 'aborted'

export type ChatRecoveryStatus = {
  reason: ChatRecoveryReason
  message: string
  continuable: boolean
  serverMessage?: string
}

export type ChatMessage = {
  id: string
  role: 'user' | 'assistant' | 'summary' | 'continuation'
  text: string
  attachments?: AttachmentMeta[]
  references?: ChatReference[]
  thinking?: string
  contentBlocks?: AiContentBlock[]
  thinkingDurationMs?: number
  toolCalls?: ToolActivity[]
  segments?: MessageSegment[]
  errorMessage?: string
  warningMessage?: string
  recoveryStatus?: ChatRecoveryStatus
}

export function keepLatestSummary(items: ChatMessage[]): ChatMessage[] {
  let latestSummaryIndex = -1
  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i].role === 'summary') {
      latestSummaryIndex = i
      break
    }
  }
  if (latestSummaryIndex < 0) return items
  return items.filter((message, index) => message.role !== 'summary' || index === latestSummaryIndex)
}

export type YamlStreamCallbacks = {
  onYamlStart: () => void
  onYamlUpdate: (yaml: string) => void
  onYamlComplete: (yaml: string) => void
}

export type SendMessageOptions = {
  routeContext?: string
  currentYaml?: string
  editorKind?: 'task' | 'dashboard'
  connectorIds?: number[]
  attachmentIds?: number[]
  attachments?: AttachmentMeta[]
  references?: ChatReference[]
  yamlCbs?: YamlStreamCallbacks
}

const YAML_OPEN = '```yaml\n'
const YAML_CLOSE = '```'
const YAML_CLOSE_RE = /\n```\s*(?:\n|$)/
const SIDEBAR_OPEN_KEY = 'envoy-ai-sidebar-open'
const SIDEBAR_WIDTH_KEY = 'envoy-ai-sidebar-width'
const MODEL_KEY = 'envoy-ai-model'

export const useAiChatStore = defineStore('ai-chat', () => {
  const router = useRouter()

  const sidebarOpen = ref(localStorage.getItem(SIDEBAR_OPEN_KEY) === 'true')
  const sidebarWidth = ref(Number(localStorage.getItem(SIDEBAR_WIDTH_KEY)) || 380)
  const conversationId = ref<number | null>(null)
  const conversations = ref<AiConversation[]>([])
  const messages = ref<ChatMessage[]>([])
  const isStreaming = ref(false)
  const compactionActive = ref(false)
  const contextTokens = ref<number | null>(null)
  const contextWindow = ref(200_000)
  const contextPercent = computed(() => {
    if (contextTokens.value == null || contextWindow.value <= 0) return null
    return Math.min(100, Math.round((contextTokens.value / contextWindow.value) * 100))
  })
  const currentThinking = ref('')
  const currentText = ref('')
  const aiEnabled = ref<boolean | null>(null)
  const aiUnavailableReason = ref<string | null>(null)
  const availableModels = ref<AiModelInfo[]>([])
  const defaultModel = ref<string | null>(null)
  const selectedModel = ref<string | null>(null)
  const pendingApproval = ref<PendingApproval | null>(null)
  const pendingUserQuestions = ref<PendingUserQuestions | null>(null)
  const conversationTitle = ref<string | null>(null)
  const lastSendInput = ref<{ text: string; opts?: SendMessageOptions } | null>(null)

  // Stall watchdog. Two signals with different meanings:
  // - lastStreamByteAt: any bytes on the wire, including 15s server pings. Going
  //   quiet for ~3 ping intervals means the connection itself is gone ('dead').
  // - lastModelEventAt: real model/tool events only (pings excluded). Quiet here
  //   with a live wire just means a long tool call or extended thinking ('slow').
  const lastStreamByteAt = ref(0)
  const lastModelEventAt = ref(0)
  const stallState = ref<'none' | 'slow' | 'dead'>('none')
  const canRetry = computed(() => !!lastSendInput.value)
  let stallDismissedUntil = 0
  let stallTimer: ReturnType<typeof setInterval> | null = null

  const STALL_DEAD_MS = 45_000
  const STALL_SLOW_MS = 60_000

  watch(isStreaming, streaming => {
    if (stallTimer) { clearInterval(stallTimer); stallTimer = null }
    if (!streaming) {
      stallState.value = 'none'
      return
    }
    stallTimer = setInterval(() => {
      if (!isStreaming.value) return
      const now = Date.now()
      if (now - lastStreamByteAt.value > STALL_DEAD_MS || navigator.onLine === false) {
        stallState.value = 'dead'
      } else if (now - lastModelEventAt.value > STALL_SLOW_MS && now > stallDismissedUntil) {
        stallState.value = 'slow'
      } else {
        stallState.value = 'none'
      }
    }, 5_000)
  })

  function keepWaiting() {
    stallDismissedUntil = Date.now() + 60_000
    stallState.value = 'none'
  }

  function resetStallTracking() {
    lastStreamByteAt.value = Date.now()
    lastModelEventAt.value = Date.now()
    stallState.value = 'none'
    stallDismissedUntil = 0
  }

  // References staged for the NEXT message (composer @-mentions and Add-to-chat
  // buttons anywhere in the app). Drained by sendMessage; chips in the composer
  // are the single visible representation — no inline text tokens.
  const MAX_COMPOSER_REFS = 10
  const composerRefs = ref<ChatReference[]>([])

  // Text staged for the composer input (Ask AI buttons prefill instead of
  // auto-sending). The composer watches this, copies it into its input, and
  // clears it — the user reviews and sends manually.
  const composerDraft = ref<string | null>(null)

  function addComposerRef(reference: ChatReference) {
    sidebarOpen.value = true
    if (composerRefs.value.some(r => r.type === reference.type && r.id === reference.id)) {
      toast.info(`${reference.label} is already attached`)
      return
    }
    if (composerRefs.value.length >= MAX_COMPOSER_REFS) {
      toast.info(`You can attach up to ${MAX_COMPOSER_REFS} references per message`)
      return
    }
    composerRefs.value.push(reference)
  }

  function removeComposerRef(reference: ChatReference) {
    composerRefs.value = composerRefs.value.filter(
      r => !(r.type === reference.type && r.id === reference.id),
    )
  }

  const taskEditorYaml = ref<string | undefined>()
  const yamlEditorKind = ref<'task' | 'dashboard'>('task')
  const taskEditorConnectorIds = ref<number[] | undefined>()
  const taskEditorOriginalYaml = ref<string | undefined>()
  const taskEditorPendingYaml = ref<string | undefined>()
  const taskEditorPhase = ref<'edit' | 'streaming' | 'review'>('edit')
  const yamlCallbacks = ref<YamlStreamCallbacks | null>(null)
  // The task editor is "active" exactly when a TaskEditorView has bound its
  // streaming callbacks (cleared on unmount). This single signal drives all
  // editor-only behavior — diff/apply rendering and sending the live buffer as
  // current_yaml — replacing the old agent/task-editor mode split.
  const editorActive = computed(() => yamlCallbacks.value !== null)

  let abortController: AbortController | null = null
  let compactionAbortController: AbortController | null = null
  let streamGeneration = 0
  let compactionGeneration = 0
  let conversationLoadGeneration = 0
  let thinkingStartTime = 0
  let currentSignature = ''
  let yamlBlockStarted = false
  let yamlStartIndex = -1
  // Durable-turn cursor: last observed SSE `seq` and the turn id for cancel / re-attach.
  let lastEventSeq = 0
  let activeTurnId: number | null = null
  let sawTerminalDone = false

  // Fast tools (validate_yaml, describe_api_operation, …) can report their
  // tool_call and tool_result in the same client render tick, so the "calling"
  // spinner + label never paints and the user never sees which tool ran. Hold
  // the in-progress state for a short minimum so every tool call is visible.
  const MIN_TOOL_CALLING_MS = 450
  let pendingToolFinalizers: Array<{ timer: ReturnType<typeof setTimeout>; apply: () => void; activity: ToolActivity }> = []

  function flushPendingToolFinalizers() {
    for (const f of pendingToolFinalizers) {
      clearTimeout(f.timer)
      f.apply()
    }
    pendingToolFinalizers = []
  }
  // Set when a tool_call is streamed; consumed on the next text/thinking delta.
  // The server runs tools strictly sequentially, so once model prose resumes,
  // every earlier tool in the turn has finished — a still-'calling' card at that
  // point had its tool_result dropped and must be settled so it stops spinning.
  let toolSweepPending = false
  let yamlSearchFrom = 0

  watch(sidebarOpen, v => localStorage.setItem(SIDEBAR_OPEN_KEY, String(v)))
  watch(sidebarWidth, v => localStorage.setItem(SIDEBAR_WIDTH_KEY, String(v)))

  const hasMessages = computed(() => messages.value.length > 0)
  const latestAssistantMessage = computed(() => {
    for (let i = messages.value.length - 1; i >= 0; i--) {
      const message = messages.value[i]
      if (message.role === 'assistant') return message
    }
    return undefined
  })

  function toggleSidebar(force?: boolean) {
    sidebarOpen.value = force ?? !sidebarOpen.value
  }

  function setSidebarWidth(px: number) {
    sidebarWidth.value = Math.min(560, Math.max(300, px))
  }

  async function checkAiStatus() {
    try {
      const res = await aiApi.status()
      aiEnabled.value = res.enabled
      aiUnavailableReason.value = res.unavailable_reason ?? (
        res.enabled === false ? 'AI is not configured.' : null
      )
      availableModels.value = res.models ?? []
      defaultModel.value = res.default_model ?? null
      if (res.context_window) contextWindow.value = res.context_window
      // Restore the persisted choice only if it's still in the catalog;
      // otherwise fall back to the server default.
      const saved = localStorage.getItem(MODEL_KEY)
      selectedModel.value = saved && availableModels.value.some(m => m.id === saved)
        ? saved
        : defaultModel.value
    } catch (err) {
      // Session may not exist yet (App mounts on /login before auth completes).
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        return
      }
      aiEnabled.value = false
      aiUnavailableReason.value = 'AI is not configured.'
    }
  }

  function setModel(id: string) {
    if (!availableModels.value.some(m => m.id === id)) return
    selectedModel.value = id
    localStorage.setItem(MODEL_KEY, id)
  }

  /** Model override for chat requests — omitted while on the server default so
   * clients that never picked a model track operator config changes. */
  function modelOverride(): string | undefined {
    const m = selectedModel.value
    return m && m !== defaultModel.value ? m : undefined
  }

  async function loadConversations(q?: string) {
    try {
      const res = await aiApi.listConversations({ q, limit: 50 })
      conversations.value = res.result
    } catch (err) {
      showApiError('Failed to load conversations', err)
    }
  }

  function showApiError(title: string, err: unknown, fallback = 'Please try again.') {
    if (err instanceof ApiError && err.status === 429) {
      toast.error('Too many requests', { description: err.message || 'Wait a moment and try again.' })
      return
    }
    const description = err instanceof Error && err.message ? err.message : fallback
    toast.error(title, { description })
  }

  function showCompactionConflict(action: string) {
    toast.info(`Wait for conversation summarization to finish before ${action}.`)
  }

  async function renameConversation(title: string): Promise<boolean> {
    const trimmedTitle = title.trim()
    if (!trimmedTitle) {
      toast.info('Enter a conversation title.')
      return false
    }
    const id = conversationId.value
    if (!id) {
      toast.info('Start the conversation before renaming it.')
      return false
    }
    if (compactionActive.value) {
      showCompactionConflict('renaming')
      return false
    }
    if (isStreaming.value) {
      toast.info('Stop the current response before renaming the conversation.')
      return false
    }
    try {
      const updated = await aiApi.renameConversation(id, trimmedTitle)
      if (conversationId.value === id) conversationTitle.value = updated.title
      const listed = conversations.value.find(conversation => conversation.id === id)
      if (listed) listed.title = updated.title
      return true
    } catch (err) {
      showApiError('Failed to rename conversation', err)
      return false
    }
  }

  async function deleteConversation(): Promise<boolean> {
    if (!conversationId.value) return false
    if (compactionActive.value) {
      showCompactionConflict('deleting')
      return false
    }
    const id = conversationId.value
    stopStreaming()
    try {
      await aiApi.deleteConversation(id)
      if (conversationId.value === id) {
        await startNewConversation()
      }
      await loadConversations()
      return true
    } catch (err) {
      showApiError('Failed to delete conversation', err)
      return false
    }
  }

  function parsePersistedSegments(raw?: string | null): MessageSegment[] | undefined {
    if (!raw) return undefined
    try {
      const parsed = JSON.parse(raw)
      if (!Array.isArray(parsed) || parsed.length === 0) return undefined
      return parsed as MessageSegment[]
    } catch {
      return undefined
    }
  }

  function parseUserContent(content: unknown): { text: string; attachments?: AttachmentMeta[]; references?: ChatReference[] } {
    if (typeof content === 'string') return { text: content }
    if (!Array.isArray(content)) return { text: String(content) }
    const textParts = content.filter((b: { type?: string }) => b.type === 'text') as Array<{ text: string }>
    const attachmentBlocks = content.filter((b: { type?: string }) => b.type === 'attachment') as Array<{
      attachmentId: number
      filename: string
      mime: string
      kind: 'image' | 'text'
      size?: number
    }>
    // Persisted alongside attachments by the chat route so reloads restore the
    // reference chips on the user bubble. Not a ChatReference: in the persisted
    // block 'type' is the block discriminator and the entity type is ref_type.
    const referenceBlocks = content.filter((b: { type?: string }) => b.type === 'entity_ref') as Array<{
      type: 'entity_ref'
      ref_type?: string
      id?: string | number
      label?: string
      connector_id?: number
      integration?: string
    }>
    const references = referenceBlocks
      .map(b => ({
        type: (b.ref_type ?? '') as ChatReference['type'],
        id: String(b.id ?? ''),
        label: String(b.label ?? ''),
        ...(b.connector_id !== undefined ? { connector_id: b.connector_id } : {}),
        ...(b.integration ? { integration: b.integration } : {}),
      }))
      .filter(r => r.type && r.id)
    return {
      text: textParts.map(b => b.text).join(''),
      attachments: attachmentBlocks.length
        ? attachmentBlocks.map(b => ({
          id: b.attachmentId,
          filename: b.filename,
          mime: b.mime,
          kind: b.kind,
          size: b.size ?? 0,
        }))
        : undefined,
      references: references.length ? references : undefined,
    }
  }

  function replaceSummary(message: ChatMessage, insertAt = messages.value.length) {
    const adjustedInsertAt = messages.value
      .slice(0, insertAt)
      .filter(existing => existing.role !== 'summary')
      .length
    const next = messages.value.filter(existing => existing.role !== 'summary')
    next.splice(Math.min(adjustedInsertAt, next.length), 0, message)
    messages.value = next
  }

  async function loadConversation(id: number): Promise<boolean> {
    if (compactionActive.value) {
      showCompactionConflict('switching conversations')
      return false
    }
    if (isStreaming.value) {
      toast.info('Stop the current response before switching conversations.')
      return false
    }
    const generation = ++conversationLoadGeneration
    try {
      const res = await aiApi.getConversation(id)
      if (generation !== conversationLoadGeneration) return false
      conversationId.value = id
      conversationTitle.value = res.conversation.title
      contextTokens.value = res.conversation.last_context_tokens ?? null
      messages.value = keepLatestSummary(res.messages.map(m => {
        try {
          if (m.role === 'continuation') {
            return { id: String(m.id), role: 'continuation' as const, text: 'Continued' }
          }
          if (m.role === 'summary') {
            let text = m.content
            try {
              const p = JSON.parse(m.content)
              if (typeof p === 'string') text = p
            } catch { /* raw */ }
            return { id: String(m.id), role: 'summary' as const, text }
          }
          const content = JSON.parse(m.content)
          if (typeof content === 'string') {
            return { id: String(m.id), role: m.role as 'user' | 'assistant', text: content }
          }
          if (Array.isArray(content)) {
            if (m.role === 'user') {
              const parsed = parseUserContent(content)
              return {
                id: String(m.id),
                role: 'user' as const,
                text: parsed.text,
                attachments: parsed.attachments,
                references: parsed.references,
              }
            }

            const textParts = content.filter((b: { type?: string }) => b.type === 'text') as Array<{ text: string }>
            const thinkingBlock = content.find((b: { type?: string }) => b.type === 'thinking') as { thinking?: string } | undefined
            const text = textParts.map(b => b.text).join('')

            const persisted = parsePersistedSegments(m.segments)
            const persistedRecovery = persisted?.find(
              (s): s is Extract<MessageSegment, { type: 'recovery' }> => s.type === 'recovery',
            )
            // Persisted history should never show a stuck spinner — the server
            // finalizes segments on save, but repair any legacy/interrupted rows.
            if (persisted) {
              for (const seg of persisted) {
                if (seg.type === 'tool_call' && seg.activity.status === 'calling') {
                  seg.activity.status = 'done'
                }
              }
            }
            return {
              id: String(m.id),
              role: m.role as 'user' | 'assistant',
              text,
              thinking: thinkingBlock?.thinking,
              contentBlocks: content,
              segments: persisted ?? textParts.map(b => ({ type: 'text' as const, text: b.text })),
              toolCalls: persisted
                ?.filter((s): s is Extract<MessageSegment, { type: 'tool_call' }> => s.type === 'tool_call')
                .map(s => s.activity),
              recoveryStatus: persistedRecovery
                ? recoveryStatus(
                    persistedRecovery.status.reason,
                    persistedRecovery.status.message,
                    persistedRecovery.status.continuable,
                  )
                : undefined,
            }
          }
        } catch { /* fall through */ }
        return {
          id: String(m.id),
          role: m.role as 'user' | 'assistant' | 'summary' | 'continuation',
          text: m.content,
        }
      }))
      pendingApproval.value = res.pending?.approval ?? null
      pendingUserQuestions.value = res.pending?.user_questions ?? null
      // Staged refs belong to the conversation the user staged them in.
      composerRefs.value = []

      // Durable turn still running server-side — re-attach the SSE tail into a
      // fresh assistant bubble. Waiting turns already persist their assistant
      // row + pending cards; don't replay those into a duplicate bubble.
      activeTurnId = res.active_turn?.id ?? null
      lastEventSeq = 0
      sawTerminalDone = false
      if (res.active_turn?.status === 'running') {
        messages.value.push({
          id: crypto.randomUUID(),
          role: 'assistant',
          text: '',
          thinking: '',
          toolCalls: [],
          segments: [],
        })
        isStreaming.value = true
        pendingApproval.value = res.pending?.approval ?? null
        pendingUserQuestions.value = res.pending?.user_questions ?? null
        currentThinking.value = ''
        currentText.value = ''
        currentSignature = ''
        thinkingStartTime = Date.now()
        yamlBlockStarted = false
        yamlStartIndex = -1
        yamlSearchFrom = 0
        toolSweepPending = false
        resetStallTracking()
        const streamGen = ++streamGeneration
        const controller = new AbortController()
        abortController = controller
        const assistantMsg = messages.value[messages.value.length - 1]
        try {
          // after=0: refresh has no client cursor; replay the turn log into the
          // new bubble (assistant row is only persisted at turn end).
          await streamTurnRequest(res.active_turn.id, 0, controller.signal, assistantMsg, streamGen)
        } catch (err) {
          if ((err as Error).name === 'AbortError' || streamGen !== streamGeneration) {
            /* stopped or superseded */
          } else if (assistantMsg?.role === 'assistant') {
            assistantMsg.errorMessage = (err as Error).message || 'network error'
            if (!assistantMsg.recoveryStatus) {
              assistantMsg.recoveryStatus = recoveryStatus('error', assistantMsg.errorMessage, true)
            }
          }
        } finally {
          if (streamGen === streamGeneration) {
            finalizeYamlBlock()
            isStreaming.value = false
            compactionActive.value = false
            stallState.value = 'none'
            abortController = null
            if (sawTerminalDone) {
              activeTurnId = null
              lastEventSeq = 0
            }
          }
        }
      }
      return true
    } catch (err) {
      if (generation === conversationLoadGeneration) showApiError('Failed to load conversation', err)
      return false
    }
  }

  async function startNewConversation(): Promise<boolean> {
    if (compactionActive.value) {
      showCompactionConflict('starting a new chat')
      return false
    }
    if (isStreaming.value) stopStreaming()
    conversationLoadGeneration++
    conversationId.value = null
    conversationTitle.value = null
    messages.value = []
    contextTokens.value = null
    pendingApproval.value = null
    pendingUserQuestions.value = null
    activeTurnId = null
    lastEventSeq = 0
    sawTerminalDone = false
    // Staged refs belong to the conversation the user staged them in.
    composerRefs.value = []
    return true
  }

  function buildApiMessages(): { role: 'user' | 'assistant'; content: string | AiContentBlock[] }[] {
    return messages.value
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .map(m => {
        if (m.role === 'user') return { role: 'user' as const, content: m.text }
        return {
          role: 'assistant' as const,
          content: m.contentBlocks || [{ type: 'text' as const, text: m.text }],
        }
      })
  }

  function checkYamlStream() {
    const cbs = yamlCallbacks.value
    if (!cbs) return
    const text = currentText.value

    while (true) {
      if (!yamlBlockStarted) {
        const markerIdx = text.indexOf(YAML_OPEN, yamlSearchFrom)
        if (markerIdx < 0) return
        yamlBlockStarted = true
        yamlStartIndex = markerIdx + YAML_OPEN.length
        cbs.onYamlStart()
      }

      const afterMarker = text.slice(yamlStartIndex)
      const closeMatch = afterMarker.match(YAML_CLOSE_RE)
      if (closeMatch && closeMatch.index !== undefined) {
        cbs.onYamlComplete(afterMarker.slice(0, closeMatch.index).trim())
        yamlSearchFrom = yamlStartIndex + closeMatch.index + closeMatch[0].length
        yamlBlockStarted = false
        yamlStartIndex = -1
        continue
      }

      cbs.onYamlUpdate(afterMarker.trim())
      return
    }
  }

  function finalizeYamlBlock() {
    const cbs = yamlCallbacks.value
    if (!cbs) return
    if (yamlBlockStarted) {
      const afterMarker = currentText.value.slice(yamlStartIndex)
      const closeMatch = afterMarker.match(YAML_CLOSE_RE)
      const yamlContent = closeMatch && closeMatch.index !== undefined
        ? afterMarker.slice(0, closeMatch.index).trim()
        : afterMarker.trim()
      if (yamlContent) cbs.onYamlComplete(yamlContent)
    }
    yamlBlockStarted = false
    yamlStartIndex = -1
    yamlSearchFrom = 0
  }

  // Collect tool activities from both `segments` (what the UI renders) and
  // `toolCalls` (legacy mirror). Prefer segments — they are authoritative.
  function toolActivitiesForMessage(m: ChatMessage): ToolActivity[] {
    const seen = new Set<ToolActivity>()
    const out: ToolActivity[] = []
    for (const seg of m.segments ?? []) {
      if (seg.type === 'tool_call' && !seen.has(seg.activity)) {
        seen.add(seg.activity)
        out.push(seg.activity)
      }
    }
    for (const tc of m.toolCalls ?? []) {
      if (!seen.has(tc)) {
        seen.add(tc)
        out.push(tc)
      }
    }
    return out
  }

  // Match a streamed tool_result back to the tool_call segment it completes.
  // Usually that segment lives in the assistant message currently streaming —
  // but across an approval→resume boundary the approved call_api's tool_call was
  // emitted (and its spinner shown) in the EARLIER, interrupted assistant
  // message, while its tool_result only arrives in the new resumed one. Search
  // recent assistant messages newest-first by the unique tool_use_id so that
  // original segment resolves to 'done' instead of spinning forever. Prefer the
  // newest still-calling match when duplicate ids exist. The activity object is
  // shared between `toolCalls` and the rendered segment, so mutating it in place
  // updates the card wherever it lives.
  function findToolActivity(
    toolUseId: string | undefined,
    name: string,
    msg: ChatMessage,
  ): ToolActivity | undefined {
    if (toolUseId) {
      for (let i = messages.value.length - 1; i >= 0; i--) {
        const m = messages.value[i]
        if (m.role !== 'assistant') continue
        const matches = toolActivitiesForMessage(m).filter(t => t.toolUseId === toolUseId)
        if (!matches.length) continue
        return [...matches].reverse().find(t => t.status === 'calling') ?? matches[matches.length - 1]
      }
      // Id miss: the tool_call SSE line that would have created this activity was
      // dropped or parse-skipped. Rather than discard the result (leaving no card
      // to flip to 'done'), fall through to the name-based search below so the
      // most recent still-calling activity with this name gets resolved.
    }
    const ordered = [
      msg,
      ...messages.value.filter(m => m !== msg && m.role === 'assistant').reverse(),
    ]
    for (const m of ordered) {
      const match = [...toolActivitiesForMessage(m)].reverse().find(t => t.name === name && t.status === 'calling')
      if (match) return match
    }
    return undefined
  }

  // Mirror the server segment collector's finalize(): any tool still marked
  // 'calling' when the stream ends was either answered without a matching
  // tool_result event or the client failed to pair them — never leave a spinner.
  // `exceptToolUseId` keeps exactly one card spinning: the tool that is genuinely
  // pending user approval (its result only arrives after resume). Everything else
  // is settled, even mid-approval, so an unrelated earlier tool doesn't spin
  // forever behind the approval card.
  function finalizeLingeringToolCalls(scope?: ChatMessage, exceptToolUseId?: string) {
    const targets = scope
      ? [scope]
      : messages.value.filter(m => m.role === 'assistant')
    for (const m of targets) {
      for (const tc of toolActivitiesForMessage(m)) {
        if (tc.status === 'calling' && tc.toolUseId !== exceptToolUseId) tc.status = 'done'
      }
    }
  }

  // Mid-stream sweep: when model prose resumes after a tool batch, settle any
  // still-'calling' card that isn't (a) held in its minimum-visibility window or
  // (b) the tool genuinely paused on user approval. Those are cards whose
  // tool_result never arrived; without this they spin until the stream ends.
  function sweepSettledToolCalls() {
    const held = new Set(pendingToolFinalizers.map(f => f.activity))
    const exceptId = pendingApproval.value?.tool_use_id
      ?? pendingUserQuestions.value?.tool_use_id
    for (const m of messages.value) {
      if (m.role !== 'assistant') continue
      for (const tc of toolActivitiesForMessage(m)) {
        if (tc.status !== 'calling') continue
        if (held.has(tc)) continue
        if (exceptId && tc.toolUseId === exceptId) continue
        tc.status = 'done'
      }
    }
  }

  function recoveryStatus(
    reason: ChatRecoveryReason,
    serverMessage: unknown,
    continuable: unknown,
  ): ChatRecoveryStatus {
    const providedMessage = typeof serverMessage === 'string' && serverMessage.trim()
      ? serverMessage.trim()
      : undefined
    const friendlyMessages: Record<ChatRecoveryReason, string> = {
      max_tokens: 'Response reached its output limit.',
      iteration_budget: 'Paused after the available tool steps.',
      context_full: 'Conversation is near the context limit. Earlier messages will be summarized before continuing.',
      aborted: 'Response stopped.',
      error: providedMessage ?? 'Something went wrong.',
    }
    return {
      reason,
      message: friendlyMessages[reason],
      continuable: typeof continuable === 'boolean' ? continuable : reason !== 'error',
      ...(providedMessage && reason !== 'error' ? { serverMessage: providedMessage } : {}),
    }
  }

  function noteSseCursor(data: Record<string, unknown>) {
    if (typeof data.seq === 'number' && Number.isFinite(data.seq)) {
      lastEventSeq = data.seq
    }
    if (typeof data.turn_id === 'number' && Number.isFinite(data.turn_id)) {
      activeTurnId = data.turn_id
    }
  }

  async function syncActiveTurnFromConversation(id: number) {
    try {
      const res = await aiApi.getConversation(id)
      if (res.active_turn) {
        activeTurnId = res.active_turn.id
      }
    } catch {
      /* non-fatal — cancel/re-attach can look up again */
    }
  }

  function handleSseEvent(event: string, data: Record<string, unknown>, msg: ChatMessage) {
    // Server liveness pings prove the wire is open but say nothing about model
    // progress — track real events only, so the 'slow' notice still fires during
    // a silent-but-connected stall.
    if (event === 'ping') return
    lastModelEventAt.value = Date.now()
    noteSseCursor(data)

    if (!msg.segments) msg.segments = []
    const segs = msg.segments

    switch (event) {
      case 'conversation':
        conversationId.value = data.conversation_id as number
        if (typeof data.turn_id === 'number') activeTurnId = data.turn_id
        else void syncActiveTurnFromConversation(conversationId.value)
        break
      case 'title':
        conversationTitle.value = data.title as string
        void loadConversations()
        break
      case 'usage':
        contextTokens.value = data.context_tokens as number
        if (typeof data.context_window === 'number') contextWindow.value = data.context_window
        break
      case 'compaction': {
        const status = data.status as string
        if (status === 'start') {
          compactionActive.value = true
        } else {
          compactionActive.value = false
          if (status === 'done') {
            // Insert the divider before the streaming assistant placeholder so live
            // order matches the persisted seq order (user, summary, assistant).
            replaceSummary({
              id: String(data.message_id ?? crypto.randomUUID()),
              role: 'summary',
              text: (data.summary as string) ?? '',
            }, messages.value.length - 1)
          } else if (status === 'failed') {
            msg.warningMessage = String(
              data.message || 'Could not summarize earlier messages. The current response will continue.',
            )
          }
        }
        break
      }
      case 'thinking':
        if (toolSweepPending) { sweepSettledToolCalls(); toolSweepPending = false }
        currentThinking.value += data.text as string
        msg.thinking = currentThinking.value
        break
      case 'thinking_signature':
        currentSignature = data.signature as string
        break
      case 'text': {
        if (toolSweepPending) { sweepSettledToolCalls(); toolSweepPending = false }
        const delta = data.text as string
        currentText.value += delta
        msg.text = currentText.value
        const last = segs[segs.length - 1]
        if (last?.type === 'text') last.text += delta
        else segs.push({ type: 'text', text: delta })
        checkYamlStream()
        break
      }
      // The model opened a tool_use block mid-stream — its args are still being
      // generated server-side. Surface the spinner card NOW so the panel doesn't
      // look frozen during the input-generation gap; the matching 'tool_call'
      // event fills in the args when the tool actually executes.
      case 'tool_call_start': {
        const activity: ToolActivity = {
          name: data.name as string,
          toolUseId: data.tool_use_id as string,
          status: 'calling',
          startedAt: Date.now(),
        }
        if (!msg.toolCalls) msg.toolCalls = []
        msg.toolCalls.push(activity)
        segs.push({ type: 'tool_call', activity })
        break
      }
      case 'tool_call': {
        const toolUseId = data.tool_use_id as string | undefined
        // Usually announced earlier via tool_call_start — update that card in
        // place rather than duplicating it.
        const existing = toolUseId
          ? msg.toolCalls?.find(t => t.toolUseId === toolUseId && t.status === 'calling')
          : undefined
        if (existing) {
          existing.args = data.args as Record<string, unknown>
        } else {
          const activity: ToolActivity = {
            name: data.name as string,
            toolUseId,
            args: data.args as Record<string, unknown>,
            status: 'calling',
            startedAt: Date.now(),
          }
          if (!msg.toolCalls) msg.toolCalls = []
          msg.toolCalls.push(activity)
          segs.push({ type: 'tool_call', activity })
        }
        toolSweepPending = true
        break
      }
      case 'tool_result': {
        const name = data.name as string
        const toolUseId = data.tool_use_id as string | undefined
        const tc = findToolActivity(toolUseId, name, msg)
        if (tc) {
          const summary = data.summary as string
          const result = data.result
          const apply = () => {
            tc.status = 'done'
            tc.summary = summary
            tc.result = result
          }
          const elapsed = tc.startedAt ? Date.now() - tc.startedAt : MIN_TOOL_CALLING_MS
          if (elapsed >= MIN_TOOL_CALLING_MS) {
            apply()
          } else {
            // Keep the spinner + "<tool>…" label up for the remaining time so a
            // sub-tick-fast tool still shows as in-progress before collapsing.
            const timer = setTimeout(() => {
              pendingToolFinalizers = pendingToolFinalizers.filter(f => f.timer !== timer)
              apply()
            }, MIN_TOOL_CALLING_MS - elapsed)
            pendingToolFinalizers.push({ timer, apply, activity: tc })
          }
        }
        break
      }
      case 'pending_approval':
        pendingApproval.value = {
          tool_use_id: data.tool_use_id as string,
          method: data.method as string,
          path: data.path as string,
          args: data.args as PendingApproval['args'],
          summary: data.summary as string,
          rememberable: data.rememberable as boolean | undefined,
        }
        pendingUserQuestions.value = null
        break
      case 'pending_user_questions':
        pendingUserQuestions.value = {
          tool_use_id: data.tool_use_id as string,
          title: data.title as string | undefined,
          questions: data.questions as PendingUserQuestions['questions'],
        }
        pendingApproval.value = null
        break
      case 'server_tool_use': {
        const toolUseId = data.tool_use_id as string | undefined
        const existing = toolUseId
          ? msg.toolCalls?.find(t => t.toolUseId === toolUseId && t.status === 'calling')
          : undefined
        if (existing) {
          existing.args = data.args as Record<string, unknown>
          existing.name = data.name as string
        } else {
          const activity: ToolActivity = {
            name: data.name as string,
            toolUseId,
            args: data.args as Record<string, unknown>,
            status: 'calling',
            startedAt: Date.now(),
          }
          if (!msg.toolCalls) msg.toolCalls = []
          msg.toolCalls.push(activity)
          segs.push({ type: 'tool_call', activity })
        }
        toolSweepPending = true
        break
      }
      case 'web_search_tool_result':
      case 'web_fetch_tool_result': {
        const name = event === 'web_search_tool_result' ? 'web_search' : 'web_fetch'
        const toolUseId = data.tool_use_id as string | undefined
        const tc = findToolActivity(toolUseId, name, msg)
        if (tc) {
          const summary = data.summary as string
          const result = data.result
          const apply = () => {
            tc.status = 'done'
            tc.summary = summary
            tc.result = result
          }
          const elapsed = tc.startedAt ? Date.now() - tc.startedAt : MIN_TOOL_CALLING_MS
          if (elapsed >= MIN_TOOL_CALLING_MS) {
            apply()
          } else {
            const timer = setTimeout(() => {
              pendingToolFinalizers = pendingToolFinalizers.filter(f => f.timer !== timer)
              apply()
            }, MIN_TOOL_CALLING_MS - elapsed)
            pendingToolFinalizers.push({ timer, apply, activity: tc })
          }
        }
        break
      }
      case 'navigate': {
        // Preserve the ?ai=<conversationId> param across agent navigation. A
        // bare router.push(path) wipes the query string, and since
        // conversationId doesn't change, the App.vue sync watcher won't re-add
        // it — so a later refresh on the destination route would fail to
        // restore this conversation even though its messages are now persisted
        // server-side. Resolve first so a `to` that carries its own query/hash
        // is handled correctly.
        const resolved = router.resolve(data.to as string)
        const query = { ...resolved.query }
        if (conversationId.value != null && query.ai == null) {
          query.ai = String(conversationId.value)
        }
        router.push({ path: resolved.path, query, hash: resolved.hash })
        break
      }
      case 'resource':
        segs.push({ type: 'resource', refs: data.refs as ResourceRef[] })
        break
      case 'turn_status': {
        const reason = (data.reason ?? data.status) as string
        if (reason === 'done') break
        if (
          reason === 'max_tokens'
          || reason === 'iteration_budget'
          || reason === 'context_full'
          || reason === 'error'
          || reason === 'aborted'
        ) {
          msg.recoveryStatus = recoveryStatus(reason, data.message, data.continuable)
        }
        break
      }
      case 'done': {
        sawTerminalDone = true
        const blocks: AiContentBlock[] = []
        if (msg.thinking) blocks.push({ type: 'thinking', thinking: msg.thinking, signature: currentSignature })
        blocks.push({ type: 'text', text: msg.text })
        msg.contentBlocks = blocks
        if (thinkingStartTime && msg.thinking) {
          msg.thinkingDurationMs = Date.now() - thinkingStartTime
        }
        finalizeLingeringToolCalls(undefined, pendingApproval.value?.tool_use_id ?? pendingUserQuestions.value?.tool_use_id)
        break
      }
      case 'error':
        msg.errorMessage = (data.message as string) || 'Something went wrong.'
        // A preceding turn_status already described this failure with a
        // server-decided continuable flag — don't overwrite it.
        if (!msg.recoveryStatus) {
          msg.recoveryStatus = recoveryStatus('error', data.message, !!conversationId.value)
        }
        break
    }
  }

  async function consumeSseResponse(
    res: Response,
    assistantMsg: ChatMessage,
    generation: number,
  ) {
    const reader = res.body?.getReader()
    if (!reader) throw new Error('No response body')

    const decoder = new TextDecoder()
    let buffer = ''
    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        if (generation !== streamGeneration) break
        lastStreamByteAt.value = Date.now()
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        let eventType = ''
        for (const line of lines) {
          if (line.startsWith('event: ')) eventType = line.slice(7).trim()
          else if (line.startsWith('data: ') && eventType) {
            try {
              if (generation === streamGeneration) {
                const data = JSON.parse(line.slice(6)) as Record<string, unknown>
                noteSseCursor(data)
                handleSseEvent(eventType, data, assistantMsg)
              }
            } catch { /* skip */ }
            eventType = ''
          }
        }
      }
    } finally {
      try { await reader.cancel() } catch { /* ignore */ }
      reader.releaseLock()
      // The stream is over — settle any tool still held in its minimum-visible
      // window so it doesn't linger as a spinner, then clear any tool_call that
      // never received a matching tool_result (unless we're paused on approval).
      if (generation === streamGeneration) {
        flushPendingToolFinalizers()
        finalizeLingeringToolCalls(assistantMsg, pendingApproval.value?.tool_use_id ?? pendingUserQuestions.value?.tool_use_id)
      }
    }
  }

  /** Re-attach to a durable turn's event log (GET /ai/turns/:id/stream). */
  async function streamTurnRequest(
    turnId: number,
    after: number,
    signal: AbortSignal,
    assistantMsg: ChatMessage,
    generation: number,
  ) {
    activeTurnId = turnId
    let cursor = after
    while (generation === streamGeneration && !sawTerminalDone && !signal.aborted) {
      const res = await aiApi.streamTurn(turnId, cursor, signal)
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({ message: res.statusText })) as Record<string, unknown>
        const message = typeof errBody.message === 'string' && errBody.message
          ? errBody.message
          : 'Failed to re-attach to turn'
        const code = typeof errBody.error === 'string' ? errBody.error : 'AI_TURN_STREAM_FAILED'
        throw new ApiError(res.status, code, message, errBody)
      }
      await consumeSseResponse(res, assistantMsg, generation)
      cursor = lastEventSeq
      if (sawTerminalDone || signal.aborted || generation !== streamGeneration) break

      // Tail closed without `done`. Only retry while the turn is still running —
      // avoids a tight loop when the server ends a terminal/waiting tail without
      // a trailing done event.
      const convId = conversationId.value
      if (convId == null) break
      try {
        const snap = await aiApi.getConversation(convId)
        if (signal.aborted || generation !== streamGeneration) break
        if (snap.active_turn?.id !== turnId || snap.active_turn.status !== 'running') {
          break
        }
        activeTurnId = turnId
        await new Promise<void>(resolve => setTimeout(resolve, 500))
      } catch {
        break
      }
    }
    if (sawTerminalDone) activeTurnId = null
  }

  async function streamChatRequest(
    body: Record<string, unknown>,
    signal: AbortSignal,
    assistantMsg: ChatMessage,
    generation: number,
  ) {
    lastEventSeq = 0
    sawTerminalDone = false
    activeTurnId = null

    const res = await fetch('/api/ai/chat', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    })

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({ message: res.statusText })) as Record<string, unknown>
      const message = typeof errBody.message === 'string' && errBody.message
        ? errBody.message
        : 'AI request failed'
      const code = typeof errBody.error === 'string' ? errBody.error : 'AI_REQUEST_FAILED'
      throw new ApiError(res.status, code, message, errBody)
    }

    // Turn row exists before the SSE body starts — resolve id for Stop → cancel.
    if (typeof body.conversation_id === 'number') {
      void syncActiveTurnFromConversation(body.conversation_id)
    }

    await consumeSseResponse(res, assistantMsg, generation)

    // SSE tail dropped but the turn may still be running — re-attach.
    if (generation === streamGeneration && !sawTerminalDone && !signal.aborted) {
      if (activeTurnId == null && conversationId.value != null) {
        await syncActiveTurnFromConversation(conversationId.value)
      }
      if (activeTurnId != null) {
        await streamTurnRequest(activeTurnId, lastEventSeq, signal, assistantMsg, generation)
      }
    }
    if (sawTerminalDone) activeTurnId = null
  }

  async function sendMessage(text: string, opts?: SendMessageOptions): Promise<boolean> {
    const hasText = !!text.trim()
    const hasAttachments = !!opts?.attachmentIds?.length
    // References count as content: an "Add to chat" send with no typed text is
    // a valid message. Peek before the early return; drain only when sending.
    const stagedReferences = opts?.references ?? composerRefs.value.slice()
    if (compactionActive.value) {
      if (hasText) composerDraft.value = text
      showCompactionConflict('sending another message')
      return false
    }
    if (isStreaming.value || (!hasText && !hasAttachments && !stagedReferences.length)) return false

    // Drain staged references into this send. Writing them back into opts means
    // lastSendInput keeps them for retryLast() even though composerRefs is now
    // empty (a retry replays opts, not the composer state).
    const references = stagedReferences
    composerRefs.value = []
    opts = references.length ? { ...opts, references } : opts

    // Remember the input so a failed send can be replayed via retryLast().
    lastSendInput.value = { text, opts }

    if (opts?.currentYaml) taskEditorYaml.value = opts.currentYaml
    if (opts?.yamlCbs) yamlCallbacks.value = opts.yamlCbs
    if (opts?.connectorIds) taskEditorConnectorIds.value = opts.connectorIds

    messages.value.push({
      id: crypto.randomUUID(),
      role: 'user',
      text: text.trim(),
      attachments: opts?.attachments,
      references: references.length ? references : undefined,
    })
    messages.value.push({
      id: crypto.randomUUID(),
      role: 'assistant',
      text: '',
      thinking: '',
      toolCalls: [],
      segments: [],
    })

    isStreaming.value = true
    pendingApproval.value = null
    pendingUserQuestions.value = null
    currentThinking.value = ''
    currentText.value = ''
    currentSignature = ''
    thinkingStartTime = Date.now()
    yamlBlockStarted = false
    yamlStartIndex = -1
    yamlSearchFrom = 0
    toolSweepPending = false
    resetStallTracking()
    const generation = ++streamGeneration
    const controller = new AbortController()
    abortController = controller
    const assistantMsg = messages.value[messages.value.length - 1]

    try {
      await streamChatRequest({
        conversation_id: conversationId.value ?? undefined,
        message: hasText ? text.trim() : undefined,
        attachment_ids: opts?.attachmentIds,
        references: references.length ? references : undefined,
        route_context: opts?.routeContext,
        // Only send the buffer when the editor is bound; the presence of this
        // field (even an empty string) is what tells the server the task editor
        // is active and turns on the YAML apply/diff contract.
        current_yaml: editorActive.value ? (opts?.currentYaml ?? taskEditorYaml.value ?? '') : undefined,
        editor_kind: editorActive.value ? (opts?.editorKind ?? yamlEditorKind.value) : undefined,
        connector_ids: opts?.connectorIds ?? taskEditorConnectorIds.value,
        model: modelOverride(),
      }, controller.signal, assistantMsg, generation)
    } catch (err) {
      if ((err as Error).name === 'AbortError' || generation !== streamGeneration) return false
      if (assistantMsg?.role === 'assistant') {
        assistantMsg.errorMessage = (err as Error).message || 'network error'
        if (!assistantMsg.recoveryStatus) {
          assistantMsg.recoveryStatus = recoveryStatus('error', assistantMsg.errorMessage, !!conversationId.value)
        }
      }
      if (err instanceof ApiError) showApiError('AI request failed', err)
      return false
    } finally {
      if (generation === streamGeneration) {
        finalizeYamlBlock()
        isStreaming.value = false
        compactionActive.value = false
        stallState.value = 'none'
        abortController = null
      }
    }
    return true
  }

  async function retryLast() {
    if (compactionActive.value) {
      showCompactionConflict('retrying')
      return
    }
    if (isStreaming.value || !lastSendInput.value) return
    // Drop the failed assistant turn (and the user message that triggered it);
    // sendMessage re-adds the user bubble and a fresh assistant placeholder.
    while (messages.value.length && messages.value[messages.value.length - 1].role === 'assistant') {
      messages.value.pop()
    }
    if (messages.value[messages.value.length - 1]?.role === 'user') {
      messages.value.pop()
    }
    const { text, opts } = lastSendInput.value
    await sendMessage(text, opts)
  }

  async function resumeWithApproval(
    decision: 'approved' | 'rejected',
    routeContext?: string,
    opts?: { remember?: boolean },
  ) {
    if (compactionActive.value) {
      showCompactionConflict('responding to the approval')
      return
    }
    if (!pendingApproval.value || !conversationId.value || isStreaming.value) return
    const pending = pendingApproval.value
    pendingApproval.value = null
    pendingUserQuestions.value = null

    messages.value.push({
      id: crypto.randomUUID(),
      role: 'assistant',
      text: '',
      thinking: '',
      toolCalls: [],
      segments: [],
    })

    isStreaming.value = true
    const generation = ++streamGeneration
    const controller = new AbortController()
    abortController = controller
    const assistantMsg = messages.value[messages.value.length - 1]
    currentThinking.value = ''
    currentText.value = ''
    currentSignature = ''
    thinkingStartTime = Date.now()
    yamlBlockStarted = false
    yamlStartIndex = -1
    yamlSearchFrom = 0
    toolSweepPending = false
    resetStallTracking()

    try {
      await streamChatRequest({
        conversation_id: conversationId.value,
        approvals: [{
          tool_use_id: pending.tool_use_id,
          decision,
          method: pending.method,
          path: pending.path,
          query: pending.args?.query,
          body: pending.args?.body,
          remember: opts?.remember,
        }],
        route_context: routeContext,
        // Preserve editor awareness across an approval-resume so the continuation
        // still sees the live buffer and honors the YAML apply/diff contract.
        current_yaml: editorActive.value ? (taskEditorYaml.value ?? '') : undefined,
        editor_kind: editorActive.value ? yamlEditorKind.value : undefined,
        model: modelOverride(),
      }, controller.signal, assistantMsg, generation)
    } catch (err) {
      if ((err as Error).name !== 'AbortError' && generation === streamGeneration) {
        if (assistantMsg?.role === 'assistant') assistantMsg.text = `Error: ${(err as Error).message}`
        if (err instanceof ApiError) showApiError('AI request failed', err)
      }
    } finally {
      if (generation === streamGeneration) {
        finalizeYamlBlock()
        isStreaming.value = false
        stallState.value = 'none'
        abortController = null
      }
    }
  }

  async function resumeWithAnswers(
    answers: AskUserAnswer[],
    routeContext?: string,
  ) {
    if (compactionActive.value) {
      showCompactionConflict('answering questions')
      return
    }
    if (!pendingUserQuestions.value || !conversationId.value || isStreaming.value) return
    const pending = pendingUserQuestions.value
    pendingUserQuestions.value = null
    pendingApproval.value = null

    messages.value.push({
      id: crypto.randomUUID(),
      role: 'assistant',
      text: '',
      thinking: '',
      toolCalls: [],
      segments: [],
    })

    isStreaming.value = true
    const generation = ++streamGeneration
    const controller = new AbortController()
    abortController = controller
    const assistantMsg = messages.value[messages.value.length - 1]
    currentThinking.value = ''
    currentText.value = ''
    currentSignature = ''
    thinkingStartTime = Date.now()
    yamlBlockStarted = false
    yamlStartIndex = -1
    yamlSearchFrom = 0
    toolSweepPending = false
    resetStallTracking()

    try {
      await streamChatRequest({
        conversation_id: conversationId.value,
        user_answers: {
          tool_use_id: pending.tool_use_id,
          answers,
        },
        route_context: routeContext,
        current_yaml: editorActive.value ? (taskEditorYaml.value ?? '') : undefined,
        editor_kind: editorActive.value ? yamlEditorKind.value : undefined,
        model: modelOverride(),
      }, controller.signal, assistantMsg, generation)
    } catch (err) {
      if ((err as Error).name !== 'AbortError' && generation === streamGeneration) {
        // Restore the questions card so a validation/400 failure does not strand
        // the turn — the durable pending row was not consumed.
        pendingUserQuestions.value = pending
        if (assistantMsg?.role === 'assistant') {
          const idx = messages.value.lastIndexOf(assistantMsg)
          if (idx >= 0 && !assistantMsg.text && !(assistantMsg.segments?.length)) {
            messages.value.splice(idx, 1)
          } else {
            assistantMsg.text = `Error: ${(err as Error).message}`
          }
        }
        if (err instanceof ApiError) showApiError('AI request failed', err)
      }
    } finally {
      if (generation === streamGeneration) {
        finalizeYamlBlock()
        isStreaming.value = false
        stallState.value = 'none'
        abortController = null
      }
    }
  }

  async function continueTurn(routeContext?: string) {
    if (compactionActive.value) {
      showCompactionConflict('continuing')
      return
    }
    if (!conversationId.value || isStreaming.value) return

    messages.value.push({
      id: crypto.randomUUID(),
      role: 'continuation',
      text: 'Continued',
    })
    messages.value.push({
      id: crypto.randomUUID(),
      role: 'assistant',
      text: '',
      thinking: '',
      toolCalls: [],
      segments: [],
    })

    isStreaming.value = true
    pendingApproval.value = null
    pendingUserQuestions.value = null
    currentThinking.value = ''
    currentText.value = ''
    currentSignature = ''
    thinkingStartTime = Date.now()
    yamlBlockStarted = false
    yamlStartIndex = -1
    yamlSearchFrom = 0
    toolSweepPending = false
    resetStallTracking()
    const generation = ++streamGeneration
    const controller = new AbortController()
    abortController = controller
    const assistantMsg = messages.value[messages.value.length - 1]

    try {
      await streamChatRequest({
        conversation_id: conversationId.value,
        continue_turn: true,
        route_context: routeContext,
        current_yaml: editorActive.value ? (taskEditorYaml.value ?? '') : undefined,
        editor_kind: editorActive.value ? yamlEditorKind.value : undefined,
        connector_ids: taskEditorConnectorIds.value,
        model: modelOverride(),
      }, controller.signal, assistantMsg, generation)
    } catch (err) {
      if ((err as Error).name === 'AbortError' || generation !== streamGeneration) return
      if (assistantMsg?.role === 'assistant') {
        assistantMsg.errorMessage = (err as Error).message || 'network error'
        if (!assistantMsg.recoveryStatus) {
          assistantMsg.recoveryStatus = recoveryStatus('error', assistantMsg.errorMessage, true)
        }
      }
      if (err instanceof ApiError) showApiError('AI request failed', err)
    } finally {
      if (generation === streamGeneration) {
        finalizeYamlBlock()
        isStreaming.value = false
        compactionActive.value = false
        stallState.value = 'none'
        abortController = null
      }
    }
  }

  /** Manual compaction (the /compact composer command). The initiating
   * conversation and request generation own every resulting UI mutation. */
  async function compactConversation(): Promise<boolean> {
    if (isStreaming.value) {
      toast.info('Stop the current response before summarizing the conversation.')
      return false
    }
    if (compactionActive.value) {
      showCompactionConflict('starting another summary')
      return false
    }
    const id = conversationId.value
    if (!id) {
      toast.info('Nothing to compact yet — the conversation has no history.')
      return false
    }

    const generation = ++compactionGeneration
    const controller = new AbortController()
    compactionAbortController = controller
    compactionActive.value = true
    try {
      const res = await aiApi.compactConversation(id, controller.signal)
      if (generation !== compactionGeneration || conversationId.value !== id) return false
      if (res.status === 'done') {
        replaceSummary({
          id: String(res.message_id ?? crypto.randomUUID()),
          role: 'summary',
          text: res.summary ?? '',
        })
        if (typeof res.context_tokens === 'number') contextTokens.value = res.context_tokens
        toast.success('Earlier conversation summarized')
        return true
      }
      if (res.status === 'skipped') {
        toast.info(res.message || 'Nothing to compact yet.')
        return false
      }
      toast.error('Could not summarize the conversation', {
        description: res.message || 'Please try again.',
      })
      return false
    } catch (err) {
      if ((err as Error).name === 'AbortError' || generation !== compactionGeneration) return false
      showApiError('Could not summarize the conversation', err)
      return false
    } finally {
      if (generation === compactionGeneration) {
        compactionActive.value = false
        compactionAbortController = null
      }
    }
  }

  function stopStreaming() {
    const wasStreaming = isStreaming.value
    const current = messages.value[messages.value.length - 1]
    // contentBlocks is only set by the 'done' handler — its presence means the
    // turn already finished (Stop raced the tail of the stream, e.g. title
    // generation) and there is nothing to continue. An existing recoveryStatus
    // (from a turn_status) is more specific than a generic abort — keep it.
    if (wasStreaming && current?.role === 'assistant' && !current.contentBlocks && !current.recoveryStatus) {
      current.recoveryStatus = recoveryStatus('aborted', undefined, !!conversationId.value)
    }

    // Explicit cancel — disconnect alone no longer aborts the durable turn.
    const turnId = activeTurnId
    const convId = conversationId.value
    if (wasStreaming && turnId != null) {
      void aiApi.cancelTurn(turnId).catch(() => {})
    } else if (wasStreaming && convId != null) {
      void (async () => {
        try {
          const res = await aiApi.getConversation(convId)
          if (res.active_turn?.status === 'running') {
            await aiApi.cancelTurn(res.active_turn.id)
          }
        } catch { /* ignore — local UI already finalized */ }
      })()
    }

    streamGeneration++
    abortController?.abort()
    abortController = null
    compactionGeneration++
    compactionAbortController?.abort()
    compactionAbortController = null
    isStreaming.value = false
    compactionActive.value = false
    activeTurnId = null
    sawTerminalDone = true
    flushPendingToolFinalizers()
    // Abort resolves the fetch's read loop via AbortError, which returns before
    // the stream `finally` settles cards — so any tool still 'calling' when the
    // user hits Stop would spin forever. Settle them here too.
    if (wasStreaming) finalizeLingeringToolCalls(undefined, pendingApproval.value?.tool_use_id ?? pendingUserQuestions.value?.tool_use_id)
  }

  async function clearConversation(): Promise<boolean> {
    stopStreaming()
    return startNewConversation()
  }

  function extractYamlBlocks(text: string): string[] {
    const blocks: string[] = []
    const regex = /```yaml\s*\n([\s\S]*?)```/g
    let match: RegExpExecArray | null
    while ((match = regex.exec(text)) !== null) blocks.push(match[1].trim())
    return blocks
  }

  function stripYamlBlocks(text: string): string {
    return text.replace(/```yaml\s*\n[\s\S]*?```/g, '').replace(/\n{3,}/g, '\n\n').trim()
  }

  type YamlEditorBindOptions = {
    yaml?: string
    kind?: 'task' | 'dashboard'
    connectorIds?: number[]
    callbacks?: YamlStreamCallbacks | null
  }

  function bindYamlEditor(opts?: YamlEditorBindOptions | null) {
    if (!opts) {
      taskEditorYaml.value = undefined
      yamlEditorKind.value = 'task'
      taskEditorConnectorIds.value = undefined
      yamlCallbacks.value = null
      taskEditorOriginalYaml.value = undefined
      taskEditorPendingYaml.value = undefined
      taskEditorPhase.value = 'edit'
      return
    }
    taskEditorYaml.value = opts.yaml
    yamlEditorKind.value = opts.kind ?? 'task'
    taskEditorConnectorIds.value = opts.connectorIds
    yamlCallbacks.value = opts.callbacks ?? null
  }

  function setTaskEditorContext(yaml?: string, connectorIds?: number[], cbs?: YamlStreamCallbacks | null) {
    bindYamlEditor(yaml === undefined && !cbs ? null : {
      yaml,
      kind: 'task',
      connectorIds,
      callbacks: cbs,
    })
  }

  function setTaskEditorDiffState(
    original?: string,
    pending?: string,
    phase?: 'edit' | 'streaming' | 'review',
  ) {
    if (original !== undefined) taskEditorOriginalYaml.value = original
    if (pending !== undefined) taskEditorPendingYaml.value = pending
    if (phase) taskEditorPhase.value = phase
  }

  function openWithContext(seed?: {
    message?: string
    routeContext?: string
    /** Contextual model default (e.g. Ask AI buttons). Applied to the selector
     * without persisting, so the user's saved pick returns next session. */
    model?: string
    /** Stage the message in the composer for review instead of auto-sending. */
    prefill?: boolean
  }) {
    sidebarOpen.value = true
    if (seed?.model && availableModels.value.some(m => m.id === seed.model)) {
      selectedModel.value = seed.model
    }
    if (!seed?.message) return
    if (seed.prefill) {
      composerDraft.value = seed.message
    } else {
      void sendMessage(seed.message, { routeContext: seed.routeContext })
    }
  }

  return {
    sidebarOpen,
    sidebarWidth,
    conversationId,
    conversations,
    conversationTitle,
    messages,
    isStreaming,
    compactionActive,
    contextTokens,
    contextWindow,
    contextPercent,
    currentThinking,
    currentText,
    aiEnabled,
    aiUnavailableReason,
    pendingApproval,
    pendingUserQuestions,
    stallState,
    canRetry,
    keepWaiting,
    availableModels,
    defaultModel,
    selectedModel,
    setModel,
    composerRefs,
    composerDraft,
    addComposerRef,
    removeComposerRef,
    hasMessages,
    latestAssistantMessage,
    editorActive,
    taskEditorYaml,
    yamlEditorKind,
    taskEditorConnectorIds,
    taskEditorOriginalYaml,
    taskEditorPendingYaml,
    taskEditorPhase,
    toggleSidebar,
    setSidebarWidth,
    checkAiStatus,
    loadConversations,
    renameConversation,
    deleteConversation,
    loadConversation,
    startNewConversation,
    sendMessage,
    retryLast,
    resumeWithApproval,
    resumeWithAnswers,
    continueTurn,
    compactConversation,
    stopStreaming,
    clearConversation,
    extractYamlBlocks,
    stripYamlBlocks,
    setTaskEditorContext,
    bindYamlEditor,
    setTaskEditorDiffState,
    openWithContext,
  }
})
